import React, { useEffect, useMemo, useState } from 'react';
import './MessageReactions.css';
import likeIcon from '../media/reactions/like.png';
import dislikeIcon from '../media/reactions/dislike.png';
import heartIcon from '../media/reactions/heart.png';

export const REACTION_MAP = {
  like: { icon: likeIcon, label: 'Like' },
  dislike: { icon: dislikeIcon, label: 'Dislike' },
  heart: { icon: heartIcon, label: 'Heart' },
  // laugh: { emoji: "😂", label: "Laugh" },
  // wow: { emoji: "😮", label: "Wow" },
  // sad: { emoji: "😢", label: "Sad" },
  // angry: { emoji: "😡", label: "Angry" },
};

function normalizeReactionType(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function isActiveReaction(reaction) {
  if (reaction?.is_deleted === undefined && reaction?.is_active === undefined) return true;
  const isDeleted = reaction?.is_deleted === true;
  const isActive = reaction?.is_active !== false;
  return !isDeleted && isActive;
}

function safeUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getOrCreateLocalReactorId() {
  try {
    const key = 'sure_chat_reactor_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const created = safeUuid();
    localStorage.setItem(key, created);
    return created;
  } catch {
    return safeUuid();
  }
}

function ReactionGlyph({ type }) {
  const { icon, emoji, label } = REACTION_MAP[type] || {};
  if (icon) return <img className="reaction-icon" src={icon} alt="" aria-hidden="true" />;
  return (
    <span className="reaction-emoji" role="img" aria-label={label}>
      {emoji}
    </span>
  );
}

export default function MessageReactions({
  reactions = [],
  allowedReactions = [],
  currentUserNativeId = null,
  currentReactorType = 'user',
  onSetReaction,
  onDeleteReaction,
}) {
  const [local, setLocal] = useState(Array.isArray(reactions) ? reactions : []);
  const [poppingType, setPoppingType] = useState(null);

  useEffect(() => {
    setLocal(Array.isArray(reactions) ? reactions : []);
  }, [reactions]);

  const myId = useMemo(() => {
    const v = currentUserNativeId ? String(currentUserNativeId) : '';
    return v || getOrCreateLocalReactorId();
  }, [currentUserNativeId]);

  const activeReactions = useMemo(
    () => (Array.isArray(local) ? local.filter(isActiveReaction) : []),
    [local],
  );

  const allowedList = useMemo(() => {
    const base =
      Array.isArray(allowedReactions) && allowedReactions.length > 0
        ? allowedReactions
        : Object.keys(REACTION_MAP);

    const allowed = new Set();
    for (const v of base) {
      const t = normalizeReactionType(v);
      if (REACTION_MAP[t]) allowed.add(t);
    }

    return Object.keys(REACTION_MAP).filter((t) => allowed.has(t));
  }, [allowedReactions]);

  const allowedSet = useMemo(() => new Set(allowedList), [allowedList]);

  const grouped = useMemo(() => {
    const byType = new Map();
    for (const reaction of activeReactions) {
      const reactionType = normalizeReactionType(reaction?.reaction);
      if (!REACTION_MAP[reactionType]) continue;
      if (!allowedSet.has(reactionType)) continue;
      const arr = byType.get(reactionType) || [];
      arr.push(reaction);
      byType.set(reactionType, arr);
    }

    const out = new Map();
    for (const [type, items] of byType.entries()) {
      out.set(type, {
        type,
        count: items.length,
        mine: items.some((x) => String(x?.reactor_native_id) === myId),
      });
    }
    return out;
  }, [activeReactions, myId, allowedSet]);

  const toggleReaction = async (type) => {
    const reactionType = normalizeReactionType(type);
    if (!REACTION_MAP[reactionType]) return;
    if (!allowedSet.has(reactionType)) return;

    const hadMine = activeReactions.some(
      (reaction) =>
        normalizeReactionType(reaction?.reaction) === reactionType &&
        String(reaction?.reactor_native_id) === myId,
    );

    const prev = local;

    try {
      if (hadMine) {
        setLocal(
          prev.map((reaction) => {
            if (
              isActiveReaction(reaction) &&
              normalizeReactionType(reaction?.reaction) === reactionType &&
              String(reaction?.reactor_native_id) === myId
            ) {
              return {
                ...reaction,
                is_active: false,
                is_deleted: true,
                updated_at: new Date().toISOString(),
              };
            }
            return reaction;
          }),
        );

        if (typeof onDeleteReaction === 'function') {
          await onDeleteReaction(reactionType);
        }
        return;
      }

      setLocal([
        ...prev,
        {
          key: safeUuid(),
          reaction: reactionType,
          reaction_type: currentReactorType,
          reactor_native_id: myId,
          updated_at: new Date().toISOString(),
          is_active: true,
          is_deleted: false,
        },
      ]);
      setPoppingType(reactionType);

      if (typeof onSetReaction === 'function') {
        await onSetReaction(reactionType);
      }
    } catch (e) {
      setLocal(prev);
      console.error('Reaction API failed:', e);
    }
  };

  const buildTooltip = (type, count, mine) => {
    const label = REACTION_MAP[type]?.label || '';
    if (count === 0) return label;
    if (count === 1) {
      return mine ? `You reacted with ${label}` : label;
    }
    if (mine) {
      const others = count - 1;
      return others === 1 ? `You and 1 other` : `You and ${others} others`;
    }
    return `${count} ${label}`;
  };

  if (allowedList.length === 0) return null;

  return (
    <div className="message-reactions">
      {allowedList.map((type) => {
        const group = grouped.get(type);
        const count = group ? group.count : 0;
        const mine = Boolean(group && group.mine);
        const isPopping = poppingType === type;

        return (
          <button
            key={type}
            type="button"
            className={`reaction-chip ${mine ? 'active' : ''} ${isPopping ? 'reaction-chip--pop' : ''}`}
            onClick={() => toggleReaction(type)}
            onAnimationEnd={() => {
              if (isPopping) setPoppingType(null);
            }}
            title={REACTION_MAP[type]?.label}
            aria-label={REACTION_MAP[type]?.label}
            aria-pressed={mine}
          >
            <ReactionGlyph type={type} />
            {count > 0 && <span className="reaction-count">{count}</span>}
            <span className="reaction-tooltip" role="tooltip">
              {buildTooltip(type, count, mine)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
